import { useState } from 'react';
import {
  CHEST_SIZES,
  FITS,
  FIT_LABELS,
  FORMALITY_LABELS,
  PALETTES,
  PALETTE_LABELS,
  PATTERN_LABELS,
  PATTERN_TOLERANCES,
  VIBES,
  VIBE_LABELS,
  defaultProfile,
  formatBrands,
  parseBrands,
  parseProfile,
  type Profile,
  type Vibe,
} from '../shared/profile';
import { ChipGroup, Field, NumberBox, SelectBox, Segmented, TextBox } from './controls';

const asOptions = <T extends string>(ids: readonly T[], labels: Record<T, string>) =>
  ids.map((id) => ({ id, label: labels[id] }));

const FORMALITY_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  id: n,
  label: `${n} · ${FORMALITY_LABELS[n]}`,
}));

// One screen, nine questions. Everything the scorer will ever know about taste
// is captured here, so the controls stay coarse on purpose — a brief, not a
// measurement form.
export function QuizForm({
  initial,
  onSave,
  onCancel,
  saving,
  saveError,
}: {
  initial: Profile | null;
  onSave: (profile: Profile) => void;
  onCancel?: () => void;
  saving: boolean;
  saveError: string | null;
}) {
  const [draft, setDraft] = useState<Profile>(() => initial ?? defaultProfile());
  const [brandText, setBrandText] = useState(() => formatBrands((initial ?? defaultProfile()).brands));
  const [localError, setLocalError] = useState<string | null>(null);

  const patch = (changes: Partial<Profile>) => setDraft((d) => ({ ...d, ...changes }));

  const toggleVibe = (vibe: Vibe) =>
    setDraft((d) => ({
      ...d,
      vibes: d.vibes.includes(vibe) ? d.vibes.filter((v) => v !== vibe) : [...d.vibes, vibe],
    }));

  const submit = () => {
    // Brands are only parsed out of the free-text box at submit time, so a
    // half-typed "Uniql" never becomes a stored brand mid-keystroke.
    const candidate: Profile = { ...draft, brands: parseBrands(brandText) };
    const parsed = parseProfile(candidate);
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    setLocalError(null);
    onSave(parsed.profile);
  };

  const error = localError ?? saveError;

  return (
    <section className="px-5 pb-16">
      <div className="mx-auto max-w-2xl">
        <p className="kicker">{initial ? 'Revise the brief' : 'First edition'}</p>
        <h2 className="display-sm mt-2 text-2xl">What are you actually looking for?</h2>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
          Eight questions. Every deal that comes in gets scored against these answers, so rough and
          honest beats precise and aspirational.
        </p>

        <div className="mt-8 space-y-7">
          <Field index={1} label="Sizes" hint="Leave a box empty if you don't know it offhand.">
            <div className="space-y-3">
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                <NumberBox
                  label="Neck"
                  suffix="in"
                  step="0.5"
                  placeholder="15.5"
                  value={draft.sizes.neck}
                  onChange={(neck) => patch({ sizes: { ...draft.sizes, neck } })}
                />
                <NumberBox
                  label="Waist"
                  suffix="in"
                  step="1"
                  placeholder="33"
                  value={draft.sizes.waist}
                  onChange={(waist) => patch({ sizes: { ...draft.sizes, waist } })}
                />
              </div>
              <Segmented
                label="Chest size"
                options={CHEST_SIZES.map((id) => ({ id, label: id }))}
                value={draft.sizes.chest}
                onChange={(chest) => patch({ sizes: { ...draft.sizes, chest } })}
              />
            </div>
          </Field>

          <Field index={2} label="Fit">
            <Segmented
              label="Fit"
              options={asOptions(FITS, FIT_LABELS)}
              value={draft.fit}
              onChange={(fit) => patch({ fit })}
            />
          </Field>

          <Field index={3} label="Palette">
            <Segmented
              label="Palette"
              options={asOptions(PALETTES, PALETTE_LABELS)}
              value={draft.palette}
              onChange={(palette) => patch({ palette })}
            />
          </Field>

          <Field index={4} label="Patterns">
            <Segmented
              label="Pattern tolerance"
              options={asOptions(PATTERN_TOLERANCES, PATTERN_LABELS)}
              value={draft.patternTolerance}
              onChange={(patternTolerance) => patch({ patternTolerance })}
            />
          </Field>

          <Field
            index={5}
            label="Formality"
            hint="The range you actually dress in on a weekend."
          >
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <SelectBox
                label="From"
                options={FORMALITY_OPTIONS}
                value={draft.formality.min}
                onChange={(min) =>
                  patch({ formality: { min, max: Math.max(min, draft.formality.max) } })
                }
              />
              <SelectBox
                label="To"
                options={FORMALITY_OPTIONS}
                value={draft.formality.max}
                onChange={(max) =>
                  patch({ formality: { min: Math.min(max, draft.formality.min), max } })
                }
              />
            </div>
          </Field>

          <Field
            index={6}
            label="Budget"
            hint="Under the value cap can win on price alone. The middle tier only counts when it's genuinely discounted."
          >
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <NumberBox
                label="Value"
                prefix="$"
                step="5"
                value={draft.budget.valueMaxUsd}
                onChange={(v) => patch({ budget: { ...draft.budget, valueMaxUsd: v ?? 0 } })}
              />
              <NumberBox
                label="Mid"
                prefix="$"
                step="10"
                value={draft.budget.midMaxUsd}
                onChange={(v) => patch({ budget: { ...draft.budget, midMaxUsd: v ?? 0 } })}
              />
              <NumberBox
                label="Min off"
                suffix="%"
                step="5"
                value={draft.budget.midOnlyIfDiscountPct}
                onChange={(v) =>
                  patch({ budget: { ...draft.budget, midOnlyIfDiscountPct: v ?? 0 } })
                }
              />
            </div>
          </Field>

          <Field index={7} label="Brands you like" hint="Comma separated. Leave blank if none come to mind.">
            <TextBox
              label="Brands"
              placeholder="Uniqlo, J. Crew, Todd Snyder"
              value={brandText}
              onChange={setBrandText}
            />
          </Field>

          <Field index={8} label="Vibes" hint="Pick as many as fit — or none.">
            <ChipGroup
              label="Vibes"
              options={VIBES.map((id) => ({ id, label: VIBE_LABELS[id] }))}
              values={draft.vibes}
              onToggle={toggleVibe}
            />
          </Field>

        </div>

        {/* `keywords` is deliberately not a question. It is the hook the
            Save/Dismiss feedback loop writes into — asking someone who came
            here to *discover* their taste to type it out defeats the point. */}

        {error && (
          <p role="alert" className="mt-6 border-l-2 border-accent pl-3 text-sm text-accent">
            {error}
          </p>
        )}

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-xs bg-ink px-5 py-2.5 text-sm font-medium text-paper transition-colors hover:bg-accent disabled:opacity-50"
          >
            {saving ? 'Filing…' : initial ? 'Save changes' : 'File the brief'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="font-mono text-xs tracking-wide text-ink-faint underline underline-offset-4 hover:text-ink disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
